import axios from '@/types/helpers/apiHelper';
import { Log } from '../helpers/logHelper';

// interface for the reCAPTCHA v3
interface Grecaptcha {
    execute(siteKey: string, opts: { action: string }): Promise<string>;
    ready(cb: () => void): void;
}

// Enum for the statuses that code can respond
export enum ReCaptchaStatus {
    failed,
    success,
}

// Enum for all the Actions that are used to show where the ReCaptcha request come from
// These can be seen inside of the ReCaptcha console. Add a new one when you add a new request location
export enum Actions {
    QUOTATION_REQUEST_SUBMIT = "QUOTATION_REQUEST_SUBMIT",
    QUESTION_SUBMIT = "QUESTION_SUBMIT",
}

let loadPromise: Promise<Grecaptcha> | null = null;
const siteKey: string | undefined = process.env.VUE_APP_RECAPTCHA_KEY;

// This initialized the ReCaptcha object and insert it into the window
// This need to be loaded each time before you van use the runRecaptcha function
export function loadRecaptcha(): Promise<Grecaptcha> {
    if (!siteKey) throw new Error('Missing reCAPTCHA key');
    Log.log("Recaptcha sitekey:",siteKey);

    if (!loadPromise) {
        loadPromise = new Promise<Grecaptcha>((resolve, reject) => {
            const tag = document.createElement("script");
            tag.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
            tag.async = true;
            tag.defer = true;
            tag.onload = () => {
                // Bottom-left badge, scaled down until hover
                const styleId = 'recaptcha-badge-bottomleft';
                if (!document.getElementById(styleId)) {
                    const style = document.createElement('style');
                    style.id = styleId;
                    style.textContent = `
                      :root {
                        --rc-collapsed: 68px;   /* visible width when idle */
                        --rc-full: 256px;       /* visible width when expanded */
                        --rc-scale: 1;         /* idle scale */
                      }
                      .grecaptcha-badge {
                        position: fixed !important;
                        left: 0px !important;
                        right: auto !important;
                        bottom: 20px !important;

                        transform-origin: left bottom !important;
                        transform: scale(var(--rc-scale)) !important;
                        width: calc(var(--rc-collapsed) / var(--rc-scale)) !important;
                        overflow: hidden !important;

                        transition: transform .2s ease, width .2s ease !important;
                        will-change: transform, width;
                      }
                      .grecaptcha-badge:hover {
                        transform: scale(1) !important;
                        width: var(--rc-full) !important;
                      }
                      @media (max-width: 1065px) {
                        .grecaptcha-badge {
                          transform: scale(0.85) !important;
                        }
                      }
                      @media (max-width: 900px) {
                        .grecaptcha-badge {
                          transform: scale(0.7) !important;
                        }
                      }`;
                    document.head.appendChild(style);
                }
                resolve((window as any).grecaptcha as Grecaptcha);
            }
            tag.onerror = () => reject(new Error("Failed to load reCAPTCHA"));
            document.head.appendChild(tag);
        });
    }
    return loadPromise;
}

// This code first checks if a Grecaptcha object exists,
// Then gets a ReCaptcha Token(These are only usable for 2 minutes)
// Then it verifies this code to the backend which validates the request and returns a Success or Fail
export async function runRecaptcha(requestAction: Actions): Promise<ReCaptchaStatus> {
    if (!siteKey) throw new Error('Invalid sitekey');

    const grecaptcha: Grecaptcha | null = await loadPromise;
    if (grecaptcha === null) throw new Error('reCAPTCHA not loaded');

    await new Promise<void>((resolve) => grecaptcha.ready(resolve));

    const requestToken = await grecaptcha.execute(siteKey, { action: requestAction });
    if (typeof requestToken !== 'string' || !requestToken) throw new Error('Invalid reCAPTCHA token');

    const { data } = await axios.post<boolean>('/api/recaptcha/verify', { requestToken, requestAction });
    if (data !== true) return ReCaptchaStatus.failed;

    return ReCaptchaStatus.success;
}